import React, { useEffect } from "react";
import styled from "styled-components";
import { useTranslation, Trans } from "react-i18next";
import { Flex, Switch, Divider, Typography } from "antd";
import i18n from 'i18next';

import FormHeader from "../form/FormHeader";
import FormText from "../form/FormText";
import FormTitle from "../form/FormTitle";
import FormRemark from "../form/FormRemark";
import Space from "../core/Space";
import FormButton from "../form/FormButton";
import FormLabel from "../form/FormLabel";
import FormField from "../form/FormField";
import FormInput from "../form/FormInput";
import FormCheckbox from "../form/FormCheckbox";
import FormButtonPrimary from "../form/FormButtonPrimary";
import useApplyNewStore from "../../store/useApplyNewStore";
import ShadowBox from "../core/ShadowBox";
import FormInputError from "../form/FormInputError";
import useAuthStore from "../../store/useAuthStore";
import useViewStore from "../../store/useViewStore";
import useModalStore from "../../store/useModalStore";
import formatCurrency from "../../function/formatCurrency";
import withErrorHandling from '../../function/withErrorHandling';
import { basename, isProduction } from "../../setting";
import PdfThumbnail from '../common/PdfThumbnail';
import { Image } from 'antd';
import GlobalPDFErrorBoundary from "@/components/common/ErrorBoundary/GlobalPDFErrorBoundary";
import { useMPGS } from "../../hooks/useMPGS";

function Step4() {
  const { t } = useTranslation();
  const { Text } = Typography;
  const { getPreAuthFormData, step1_section2_disease, step1_isProvideFormDetail } = useApplyNewStore();
  const { step1_section2_attendDoctor, step1_section2_isBxNetwork } = useApplyNewStore();
  const { hospitalList, step1_section2_operationloctype, step1_section2_location, step1_section2_hospitalid, step1_section2_otherhopsital, step1_section2_housingclass, step1_section3_admissiondt, step1_section3_surgeryBudgetRemark } = useApplyNewStore();
  const { step1_section3_provideMedicalExpense, step1_section3_surgeonfee, step1_section3_anesthesiologistfee, step1_section3_operatingrmfee, step1_section3_housetourfee, step1_section3_hosmisousfee, step1_section3_noofdays } = useApplyNewStore();
  const { step1_section3_surgeonfee_checkbox, step1_section3_anesthesiologistfee_checkbox, step1_section3_operatingrmfee_checkbox, step1_section3_hosmisousfee_checkbox, step1_section3_housetourfee_noofdays_checkbox } = useApplyNewStore();

  const { step2_doc, step2_upload_option1 } = useApplyNewStore();
  const { step3_email, step3_mobile } = useApplyNewStore();
  const { step3_notToPaid } = useApplyNewStore();
  const { isLogCase, goBackStep, goNextStep, setStep, fetchMpgsSession, completePayment, failPayment, applicationSubmit, needMPGSPayment, setNeedMPGSPayment } = useApplyNewStore();
  const { step1_notToPaid, step4_accept, step4_accept_error, setStep4_accept, setStep4_accept_error } = useApplyNewStore();
  const { step4_pay_accept, step4_pay_accept_error, setStep4_pay_accept, setStep4_pay_accept_error } = useApplyNewStore();
  const { isStep4Valid, validateStep4 } = useApplyNewStore();
  const { findDiseaseByCode, findSurgeriesByCode } = useApplyNewStore();
  const { step3_sync, step3_Q1, step3_Q4, step3_Q5, step3_Q6, step3_Q7 } = useApplyNewStore();
  const { isMobileView, isPadView } = useViewStore();
  const { openModalInfo } = useModalStore();

  const calculateTotal = () => {
    const surgeonfee = step1_section3_surgeonfee_checkbox && step1_section3_surgeonfee ? parseFloat(step1_section3_surgeonfee) : 0;
    const anesthesiologistfee = step1_section3_anesthesiologistfee_checkbox && step1_section3_anesthesiologistfee ? parseFloat(step1_section3_anesthesiologistfee) : 0;
    const operatingrmfee = step1_section3_operatingrmfee_checkbox && step1_section3_operatingrmfee ? parseFloat(step1_section3_operatingrmfee) : 0;
    const housetourfee = step1_section3_housetourfee_noofdays_checkbox && step1_section3_housetourfee ? parseFloat(step1_section3_housetourfee) : 0;
    const hosmisousfee = step1_section3_hosmisousfee_checkbox && step1_section3_hosmisousfee ? parseFloat(step1_section3_hosmisousfee) : 0;
    const noofdays = step1_section3_housetourfee_noofdays_checkbox && step1_section3_noofdays ? parseFloat(step1_section3_noofdays) : 0;

    return formatCurrency(surgeonfee + anesthesiologistfee + operatingrmfee + hosmisousfee + (housetourfee * noofdays));
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []); 

  const { init, pay, hideOverlay } = useMPGS({
    onComplete: async (res) => {
      try {
        await completePayment();
        const success = await applicationSubmit();
        if (success) {
          setStep("success");
          window.history.pushState({}, "", `/${basename}/${i18n.language}/apply/success`);
        } else {
          setStep("submitFail");
        }
      } catch (e) {
        setStep("submitFail");
      } finally {
        setNeedMPGSPayment(false);
      }
    },
    onCancel: async () => {
      await failPayment();
      setStep("paymentFail");
      setNeedMPGSPayment(false);
    },
    onError: async (err) => {
      await failPayment();
      setStep("paymentFail");
      setNeedMPGSPayment(false);
    },
  });

  // 初始化 MPGS
  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    if (!needMPGSPayment) return;

    const doPay = async () => {
      try {
        const { sessionId } = await fetchMpgsSession();
        const host = window.location.hostname;

        if (host === "bluecross.hashtechpro.com") {
          const { openModalConfirm } = useModalStore.getState();
          const loadFailPayment = withErrorHandling(failPayment);

          openModalConfirm({
            title: "Payment Gateway Testing",
            text: "Choose payment success or fail",
            modalConfirmPrimaryButtonText: "Success",
            modalConfirmSecondaryButtonText: "Fail",
            callback: () => {
              const finishPayment = withErrorHandling(async () => {
                await completePayment();

                const success = await applicationSubmit();

                if (success) {
                  setStep("success");
                  window.history.pushState({}, "", `/${basename}/${i18n.language}/apply/success`);
                } else {
                  setStep("submitFail");
                }
              });

              finishPayment();
            },
            cancelCallback: () => {
              setStep("paymentFail");
              loadFailPayment();
            },
          });
          // 不走真正的 MPGS
          setNeedMPGSPayment(false);
          return;
        }

        // MPGS
        pay(sessionId, "#checkout-container");
      } catch (e) {
        await failPayment();
        setStep("paymentFail");
        setNeedMPGSPayment(false);
        hideOverlay();
      }
    };

    doPay();
  }, [needMPGSPayment]);


  const dnamicTNC=()=>{ 
    const PCA= <div> 
       <h3>{t("translation:apply.step4.pcatnc.title")}</h3>  
       <ul>
         <li>{t("translation:apply.step4.pcatnc.pcatnc1")}</li>
         <li>{t("translation:apply.step4.pcatnc.pcatnc2")}</li>
         <li>{t("translation:apply.step4.pcatnc.pcatnc3")}</li>
        </ul>
    </div>

    const LOG=<div>  
       <h3>{t("translation:apply.step4.logtnc.title")}</h3>  
       <ul>
         <li>{t("translation:apply.step4.logtnc.logtnc1")}</li>
         <li>{t("translation:apply.step4.logtnc.logtnc2")}</li>
         <li>{t("translation:apply.step4.logtnc.logtnc3")}</li>
         <li>{t("translation:apply.step4.logtnc.logtnc4")}</li>
         <li>{t("translation:apply.step4.logtnc.logtnc5")}</li>
         <li>{t("translation:apply.step4.logtnc.logtnc6")}</li>
        </ul>
   </div>;
   
    return <div style={{listStyle:'none'}}> 
      {
        step1_notToPaid === "Y" ? PCA :LOG
      }
    </div>
  }
  
    const downloadPdf = (doc) => { 
       try {
        const base64Data = doc.orgUrl.split(',')[1];
        const mimeType = doc.orgUrl.split(',')[0].split(':')[1].split(';')[0];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = doc.name || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Download failed:", error);
      }
    };



  return (
    <Container>
      <PageTitle>
        <FormHeader style={{ margin: 0 }}>{t("translation:apply.step4.title")}</FormHeader>
      </PageTitle>

      <Content>
        <PreviousPageBtn onClick={goBackStep}>
          <Img24 src={`${process.env.PUBLIC_URL}/images/arrow_l.svg`} alt={t("previous")} />
          <PreviousPageBtnText>{t("previous")}</PreviousPageBtnText>
        </PreviousPageBtn>

        {step1_isProvideFormDetail &&
        <Section>
          <Flex align="center" justify="space-between" style={{ width: "100%" }}>
            <Flex gap={8} align="center">
              <Img32 src={`${process.env.PUBLIC_URL}/images/document-clip.svg`} alt={t("translation:apply.step1.section2.title")} />
              <FormTitle style={{ fontSize: 20, lineHeight: "30px" }}>{t("translation:apply.step1.section2.title")}</FormTitle>
            </Flex>
            <SaveButton style={{ width: "fit-content" }} onClick={() => {
              setStep("1");
            }}>
              <Img24 src={`${process.env.PUBLIC_URL}/images/pen.svg`} alt={t("edit")} />
            </SaveButton>
          </Flex>

          <AreaTitle orientation="left" orientationMargin="0" style={{ borderColor: 'var(--aia-bx-medium-blue-20)', margin: 0 }}>
            <Flex gap={8}>
              <Img24 src={`${process.env.PUBLIC_URL}/images/product_all-in-one.svg`} alt={t("translation:apply.step1.section2.area1.title")} />
              <FormTitle style={{ color: "var(--aia-bx-blue-3)" }}>{t("translation:apply.step1.section2.area1.title")}</FormTitle>
            </Flex>
          </AreaTitle>

          <FormText style={{ color: "var(--aia-bx-dark-blue)", fontSize: 12, lineHeight: "18px" }}><b>{t("translation:apply.step1.section2.area1.disease")}</b></FormText>

          <Flex vertical gap={16} style={{ width: "100%" }}>
            {
              step1_section2_disease.map((disease, index) => {
                const diseaseCode = disease.diseaseCode.includes("|") ? disease.diseaseCode.split("|")[0] : disease.diseaseCode;
                const diseaseOther = disease.diseaseCode.includes("|") ? disease.diseaseCode.split("|")[1] : "";
                const diseaseOption = findDiseaseByCode(diseaseCode);
                const surgeryOption = findSurgeriesByCode(diseaseCode, disease.surgeryCode.split(",").map(surgery => surgery.includes("|") ? surgery.split("|")[0] : surgery));
                const diseaseName = diseaseOption ? `${diseaseOption.diseaseZh} ${diseaseOption.diseaseEn} ${diseaseOther}` : "";

                const surgeryNamesArray = surgeryOption.map((surgery, index) => {
                  return {
                    surgeryCode: surgery.surgeryCode,
                    surgeryName: `${surgery.surgeryZh} ${surgery.surgeryEn}`,
                    surgeryOther: disease.surgeryCode.split(",")[index].includes("|") ? disease.surgeryCode.split(",")[index].split("|")[1] : ""
                  };
                });

                const surgeryNames = surgeryNamesArray.map(surgery => {
                  return `${surgery.surgeryName}${surgery.surgeryOther ? ` ${surgery.surgeryOther}` : ""}`;
                }).join(", ");

                return (
                  <Flex vertical={isMobileView} gap={16} key={diseaseName}>
                    <Flex vertical flex={1}>
                      <FormRemark style={{ fontSize: 14 }}>{t("translation:apply.step1.section2.area1.Q1")}</FormRemark>
                      <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{diseaseName}</FormText>
                    </Flex>
                    <Flex vertical flex={1}>
                      <FormRemark style={{ fontSize: 14 }}>{t("translation:apply.step1.section2.area1.Q2")}</FormRemark>
                      <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{surgeryNames}</FormText>
                    </Flex>
                  </Flex>
                )
              })
            }
          </Flex>

          <AreaTitle orientation="left" orientationMargin="0" style={{ borderColor: 'var(--aia-bx-medium-blue-20)', margin: 0 }}>
            <Flex gap={8}>
              <Img24 src={`${process.env.PUBLIC_URL}/images/product_all-in-one.svg`} alt={t("translation:apply.step1.section2.area2.title")} />
              <FormTitle style={{ color: "var(--aia-bx-blue-3)" }}>{t("translation:apply.step1.section2.area2.title")}</FormTitle>
            </Flex>
          </AreaTitle>

          <Flex vertical gap={16} style={{ width: "100%" }}>
            <Flex vertical={isMobileView} gap={16}>
              <Flex vertical flex={1}>
                <FormRemark>{t("translation:apply.step1.section2.area2.Q1")}</FormRemark>
                <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{step1_section2_attendDoctor}</FormText>
              </Flex>
              <Flex vertical flex={1}>
                <FormRemark>{t("translation:apply.step1.section2.area2.Q2")}</FormRemark>
                <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{step1_section2_isBxNetwork ? t("yes") : t("no")}</FormText>
              </Flex>
            </Flex>
          </Flex>

          <AreaTitle orientation="left" orientationMargin="0" style={{ borderColor: 'var(--aia-bx-medium-blue-20)', margin: 0 }}>
            <Flex gap={8}>
              <Img24 src={`${process.env.PUBLIC_URL}/images/medical-bed.svg`} alt={t("translation:apply.step1.section2.area3.title")} />
              <FormTitle style={{ color: "var(--aia-bx-blue-3)" }}>{t("translation:apply.step1.section2.area3.title")}</FormTitle>
            </Flex>
          </AreaTitle>

          <Flex vertical gap={16} style={{ width: "100%" }}>

            {
              step1_section2_operationloctype === "HOSPITAL" &&
              <>
                <Flex vertical={isMobileView} gap={16}>
                  <Flex vertical flex={1}>
                    <FormRemark>{t("translation:apply.step1.section2.area3.Q1")}</FormRemark>
                    <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{t("translation:apply.step1.section2.area3.Q1_Option1")}</FormText>
                  </Flex>

                  <Flex vertical flex={1}>
                    <FormRemark>{t("translation:apply.step1.section2.area3.Q2")}</FormRemark>
                    <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{step1_section2_location === "HKMU" ? t("translation:apply.step1.section2.area3.Q2_Option1") : t("translation:apply.step1.section2.area3.Q2_Option2")}</FormText>
                  </Flex>
                </Flex>

                <Flex vertical={isMobileView} gap={16}>
                  {
                    step1_section2_location === "HKMU" &&
                    <Flex vertical flex={1}>
                      <FormRemark>{t("translation:apply.step1.section2.area3.Q3")}</FormRemark>
                      <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{i18n.language === "ch" ? hospitalList.find(item => item.hid === step1_section2_hospitalid?.value)?.chi_name : hospitalList.find(item => item.hid === step1_section2_hospitalid?.value)?.eng_name}</FormText>
                    </Flex>
                  }

                  {
                    ((step1_section2_location === "HKMU" && step1_section2_hospitalid?.value === "00000000000000000000000000000000") || step1_section2_location === "OTHER") &&
                    <Flex vertical flex={1}>
                      <FormRemark>{t("translation:apply.step1.section2.area3.Q4")}</FormRemark>
                      <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{step1_section2_otherhopsital}</FormText>
                    </Flex>
                  }
                </Flex>
              </>
            }

            {
              step1_section2_operationloctype === "DAYCENTRE" &&
              <Flex vertical={isMobileView} gap={16}>
                <Flex vertical flex={1}>
                  <FormRemark>{t("translation:apply.step1.section2.area3.Q1")}</FormRemark>
                  <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{t("translation:apply.step1.section2.area3.Q1_Option2")}</FormText>
                </Flex>

                <Flex vertical flex={1}>
                  <FormRemark>{t("translation:apply.step1.section2.area3.Q4")}</FormRemark>
                  <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{step1_section2_otherhopsital}</FormText>
                </Flex>
              </Flex>
            }

            <Flex vertical={isMobileView} gap={16}>
              <Flex vertical flex={1}>
                <FormRemark>{t("translation:apply.step1.section2.area3.Q5")}</FormRemark>
                <FormText style={{ fontSize: 16, lineHeight: "26px" }}>
                  {step1_section2_housingclass === "REGULAR" && t("translation:apply.step1.section2.area3.Q5_Option1")}
                  {step1_section2_housingclass === "SEMI" && t("translation:apply.step1.section2.area3.Q5_Option2")}
                  {step1_section2_housingclass === "PRIVATE" && t("translation:apply.step1.section2.area3.Q5_Option3")}
                  {step1_section2_housingclass === "DAYCASE" && t("translation:apply.step1.section2.area3.Q5_Option4")}
                  {step1_section2_housingclass === "OUTPATIENT_CLINICAL" && t("translation:apply.step1.section2.area3.Q5_Option5")}
                </FormText>
              </Flex>
              <Flex vertical flex={1}>
                <FormRemark>{t("translation:apply.step1.section2.area3.Q6")}</FormRemark>
                <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{step1_section3_admissiondt}</FormText>
              </Flex>
            </Flex>

            <Flex vertical={isMobileView} gap={16}>
              <Flex vertical flex={1}>
                <FormRemark>{t("translation:apply.step1.section2.area3.Q7")}</FormRemark>      
                  <FormText
                    style={{
                      fontSize: 16,
                      lineHeight: '26px',
                      whiteSpace: 'normal',
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                      display: 'block',
                    }}
                  >
              {step1_section3_surgeryBudgetRemark || "-"}</FormText>
              </Flex>
            </Flex>
          </Flex>

          {
            step1_section3_provideMedicalExpense && <>
              <AreaTitle orientation="left" orientationMargin="0" style={{ borderColor: 'var(--aia-bx-medium-blue-20)', margin: 0 }}>
                <Flex gap={8}>
                  <Img24 src={`${process.env.PUBLIC_URL}/images/medical-bed.svg`} alt={t("translation:apply.step1.section2.area4.title")} />
                  <FormTitle style={{ color: "var(--aia-bx-blue-3)" }}>{t("translation:apply.step1.section2.area4.title")}</FormTitle>
                </Flex>
              </AreaTitle>
              <Flex vertical gap={16} style={{ width: "100%" }}>
                <Flex vertical={isMobileView} gap={16}>
                  <Flex vertical flex={1}>
                    <FormRemark>{t("translation:apply.step1.section2.area4.Q2_option1")}</FormRemark>
                    <FormText style={{ fontSize: 16, lineHeight: "26px" }}>HK$ {step1_section3_surgeonfee || 0}</FormText>
                  </Flex>
                  <Flex vertical flex={1}>
                    <FormRemark>{t("translation:apply.step1.section2.area4.Q2_option2")}</FormRemark>
                    <FormText style={{ fontSize: 16, lineHeight: "26px" }}>HK$ {step1_section3_anesthesiologistfee || 0}</FormText>
                  </Flex>
                </Flex>

                <Flex vertical={isMobileView} gap={16}>
                  <Flex vertical flex={1}>
                    <FormRemark>{t("translation:apply.step1.section2.area4.Q2_option3")}</FormRemark>
                    <FormText style={{ fontSize: 16, lineHeight: "26px" }}>HK$ {step1_section3_operatingrmfee || 0}</FormText>
                  </Flex>
                  <Flex vertical flex={1}>
                    <FormRemark>{t("translation:apply.step1.section2.area4.Q2_option4")}</FormRemark>
                    <FormText style={{ fontSize: 16, lineHeight: "26px" }}>HK$ {step1_section3_hosmisousfee || 0}</FormText>
                  </Flex>
                </Flex>

                <Flex vertical={isMobileView} gap={16}>
                  <Flex vertical flex={1}>
                    <FormRemark>{t("translation:apply.step1.section2.area4.Q2_option5")}</FormRemark>
                    <FormText style={{ fontSize: 16, lineHeight: "26px" }}>HK$ {step1_section3_housetourfee || 0}</FormText>
                  </Flex>
                  <Flex vertical flex={1}>
                    <FormRemark>{t("translation:apply.step1.section2.area4.Q2_option6")}</FormRemark>
                    <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{step1_section3_noofdays || 0}</FormText>
                  </Flex>
                </Flex>

                <Flex vertical={isMobileView} gap={16}>
                  <Flex vertical flex={1}>
                    <FormRemark>{t("translation:apply.step1.section2.area4.total")}</FormRemark>
                    <FormText style={{ fontSize: 16, lineHeight: "26px" }}>HK$ {calculateTotal()}</FormText>
                  </Flex>
                </Flex>
              </Flex>
            </>
          }
        </Section>
        }
        {
          step2_upload_option1 &&
          <Section>
            <Flex align="center" justify="space-between" style={{ width: "100%" }}>
              <Flex gap={8} align="center">
                <Img32 src={`${process.env.PUBLIC_URL}/images/tray-arrow-u.svg`} alt={t("translation:apply.step2.section1.title")} />
                <FormTitle style={{ fontSize: 20, lineHeight: "30px" }}>{t("translation:apply.step2.section1.title")}</FormTitle>
              </Flex>
              <SaveButton style={{ width: "fit-content" }} onClick={() => {
                setStep("2");
              }}>
                <Img24 src={`${process.env.PUBLIC_URL}/images/pen.svg`} alt={t("edit")} />
              </SaveButton>
            </Flex>

            <Flex vertical gap={8} style={{ width: "100%" }}>
              {
                step2_doc.map((doc) => {
                  return (
                    doc.contenttype === "application/pdf" ?  
                     <div key={doc.attachobjid} onClick={()=>downloadPdf(doc)} style={{ cursor: "pointer" }}>
                      <Flex gap={8} style={{ width: "100%", borderRadius: 5, border: "1px solid #ccc", padding: 8 ,overflow:"hidden",flexWrap: "wrap"}}>
                          <GlobalPDFErrorBoundary>
                             <PdfThumbnail base64={doc.orgUrl} />
                          </GlobalPDFErrorBoundary>
                          <Flex justify="center" align="center" style={{
                            color:'rgb(22, 119, 255)'
                           }}>{doc.name}</Flex>
                      </Flex>
                    </div>
                    :
                    <div key={doc.attachobjid} style={{ cursor: "pointer" }}>
                        <Flex gap={8} style={{ width: "100%", borderRadius: 5, border: "1px solid #ccc", padding: 8, cursor: "pointer" }}>                          
                          <Image src={doc.url} style={{ width: 48, height: 48, objectFit: "cover" }} />
                          <Flex justify="center" align="center" style={{
                            color:'rgb(22, 119, 255)'
                           }}>{doc.name}</Flex>                        
                        </Flex>
                    </div>
                  )
                })
              }
            </Flex>
          </Section>
        }

        <Section>
          <Flex align="center" justify="space-between" style={{ width: "100%" }}>
            <Flex gap={8} align="center">
              <Img32 src={`${process.env.PUBLIC_URL}/images/document-envelope.svg`} alt={t("translation:apply.step3.section1.title")} />
              <FormTitle style={{ fontSize: 20, lineHeight: "30px" }}>{t("translation:apply.step3.section1.title")}</FormTitle>
            </Flex>
            <SaveButton style={{ width: "fit-content" }} onClick={() => {
              setStep("3");
            }}>
              <Img24 src={`${process.env.PUBLIC_URL}/images/pen.svg`} alt={t("edit")} />
            </SaveButton>
          </Flex>

          <Flex vertical gap={16} style={{ width: "100%" }}>
            <Flex vertical={isMobileView} gap={16}>
              <Flex vertical flex={1}>
                <FormRemark>{t("translation:apply.step3.section1.Q1")}</FormRemark>
                <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{step3_email}</FormText>
              </Flex>
              <Flex vertical flex={1}>
                <FormRemark>{t("translation:apply.step3.section1.Q3")}</FormRemark>
                <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{step3_mobile}</FormText>
              </Flex>
            </Flex>
          </Flex>
        </Section>

        {
          step3_sync &&
          <Section>
            <Flex align="center" justify="space-between" style={{ width: "100%" }}>
              <Flex gap={8} align="center">
                <Img32 src={`${process.env.PUBLIC_URL}/images/pre-approval.svg`} alt={t("translation:apply.step3.section1.title")} />
                <FormTitle style={{ fontSize: 20, lineHeight: "30px" }}>{t("translation:apply.step3.section2.title")}</FormTitle>
              </Flex>
              <SaveButton style={{ width: "fit-content" }} onClick={() => {
                setStep("3");
              }}>
                <Img24 src={`${process.env.PUBLIC_URL}/images/pen.svg`} alt={t("edit")} />
              </SaveButton>
            </Flex>

            <Flex vertical gap={16} style={{ width: "100%" }}>
              <Flex vertical={isMobileView} gap={16}>
                <Flex vertical flex={1}>
                  <FormRemark>{t("translation:apply.step0.sectionLeft.Q1")}</FormRemark>
                  <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{step3_Q1}</FormText>
                </Flex>
                <Flex vertical flex={1}>
                  <FormRemark>{t("translation:apply.step0.sectionLeft.Q4")}</FormRemark>
                  <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{step3_Q4 === "person" ? t("translation:apply.step0.sectionLeft.Q4_Option1") : t("translation:apply.step0.sectionLeft.Q4_Option2")}</FormText>
                </Flex>
              </Flex>
            </Flex>

            {
              step3_Q4 === "person" &&
              <Flex vertical gap={16} style={{ width: "100%" }}>
                <Flex vertical={isMobileView} gap={16}>
                  <Flex vertical flex={1}>
                    <FormRemark>{t("translation:apply.step0.sectionLeft.Q5")}</FormRemark>
                    <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{step3_Q5}</FormText>
                  </Flex>
                </Flex>
              </Flex>
            }

            {
              step3_Q4 === "spouse_children" &&
              <Flex vertical gap={16} style={{ width: "100%" }}>
                <Flex vertical={isMobileView} gap={16}>
                  <Flex vertical flex={1}>
                    <FormRemark>{t("translation:apply.step0.sectionLeft.Q6")}</FormRemark>
                    <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{step3_Q6} ({step3_Q7})</FormText>
                  </Flex>
                  {/* <Flex vertical flex={1}>
                    <FormRemark>{t("translation:apply.step0.sectionLeft.Q7")}</FormRemark>
                    <FormText style={{ fontSize: 16, lineHeight: "26px" }}>{step3_Q7}</FormText>
                  </Flex> */}
                </Flex>
              </Flex>
            }
          </Section>
        }

        <Flex vertical>
          <FormCheckbox active={step4_accept} onClick={() => {
            setStep4_accept(!step4_accept);
            setStep4_accept_error(false);
            validateStep4(true);
          }} id="apply_step4_checkbox_agree">
            <Text><Trans i18nKey="translation:apply.step4.terms" components={{
              a1: <LinkBtn onClick={(event) => {
                event.stopPropagation();
                openModalInfo({
                  title: t("translation:apply.step4.termsConditionModal.title"),
                  text: <Flex gap={8} vertical align="flex-start">
                    {/* {
                      i18n.language === "ch" ? <>
                        {
                          // 改tnc
                          // (isLogCase() && !step1_notToPaid) ?
                          getPreAuthFormData?.form?.triggerPaymentGateway === "Y" ?
                            <>
                              <FormText style={{ marginBottom: 16 }}>1. {t("translation:apply.step4.termsConditionModal.preAuth.1")}</FormText>
                              <FormText>2. {t("translation:apply.step4.termsConditionModal.preAuth.2")}</FormText>
                            </>
                            :
                            <FormText style={{ marginBottom: 16 }}>1. {t("translation:apply.step4.termsConditionModal.PCA.1")}</FormText>
                        }
                      </>
                        :
                        <FormText style={{ marginBottom: 16 }}>1. {t("translation:apply.step4.termsConditionModal.PCA.1")}</FormText>
                    } */} 
                    {
                      dnamicTNC()
                    }
                  </Flex>,
                  style: {
                    content: {
                      width: 800
                    }
                  }
                });
              }} />,
              a2: <Link onClick={(event) => { event.stopPropagation(); }} href={"https://ap.bluecross.com.hk/shared/general/PICS_bcaia.pdf?q=Wednesday,%20October%2016,%2020242:19:53%20PM"} target="_blank" />
            }} /></Text>
          </FormCheckbox>
          <FormInputError show={step4_accept_error} style={{ width: "100%" }}>* {t("translation:apply.step4.terms_error")}</FormInputError>
        </Flex>

        {
          // (isLogCase() && !step1_notToPaid) && <>
          getPreAuthFormData?.form?.triggerPaymentGateway === "Y" && <>
            <Terms>
              <FormCheckbox active={step4_pay_accept} onClick={() => {
                setStep4_pay_accept(!step4_pay_accept);
                setStep4_pay_accept_error(false);
                validateStep4(true);
              }}>{t("translation:apply.step1.section3.term1")}</FormCheckbox>
              <Flex vertical gap={16} style={{ marginLeft: 40 }}>
                <FormText>{t("translation:apply.step1.section3.term2")}</FormText>
                <FormRemark>{t("translation:apply.step1.section3.term3")}</FormRemark>
                <FormRemark>{t("translation:apply.step1.section3.term4")}</FormRemark>
                <FormInputError show={step4_pay_accept_error}>* {t("pleaseInput", { key: t("translation:apply.step1.section3.Q1") })}</FormInputError>
              </Flex>
            </Terms>
            <ShadowBox style={{ padding: "16px 24px", gap: 8 }}>
              <Circle>i</Circle>
              <FormText style={{ flex: 1 }}>{t("translation:apply.step4.log_remark")}</FormText>
            </ShadowBox>
          </>
        }

        <Flex justify="center" style={{ width: "100%", margin: isMobileView ? "8px 0px" : "32px 0px" }}>
          <FormButtonPrimary id={isProduction ? "pre_auth_confirm_btn" : "pre_auth_uat_confirm_btn"} $disabled={!isStep4Valid} disabled={!isStep4Valid} onClick={() => {
            if (validateStep4()) {
              goNextStep();
            }
          }} style={{ width: isMobileView ? "100%" : "fit-content" }}>{(getPreAuthFormData?.form?.triggerPaymentGateway === "Y") ? t("translation:apply.step4.log_btn") : t("translation:apply.step4.pca_btn")}</FormButtonPrimary>
        </Flex>
      </Content>
    </Container >
  );
}

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PageTitle = styled.div`
  background-image: url(${process.env.PUBLIC_URL}/images/bg_light_1.png);
  width: 100%;
  height: 100px;
  background-size: cover;
  background-position: center;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PreviousPageBtnText = styled(FormText)`
  font-size: 16px;
  line-height: 26px;
  color: var(--aia-bx-dark-blue);
  font-weight: bold;
`;

const PreviousPageBtn = styled(FormButton)`
  display: flex;
  flex-direction: row;
  gap: 8px;
  padding: 8px;
  width: fit-content;
`;

const Content = styled.div`
  max-width: 800px;
  width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Section = styled(ShadowBox)`
  flex-direction: column;
  align-items: self-start;
  padding: 24px;
  gap: 16px;
`;

const Area = styled(Flex)`
  width: 100%;
`;

const AreaTitle = styled(Divider)`
  width: 100%;
  border-block-start: 0 var(--aia-bx-medium-blue-20) !important;
`;

const FormLabelBold = styled(FormLabel)`
  font-weight: bold;
`;

const SaveButton = styled.button`
  flex: 0;
  display: flex;
  align-items: center;
  background-color: transparent;
  border-radius: 10px;
  color: var(--aia-bx-blue-2);
  gap: 3px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  padding: 10px;

  &:hover {
    background-color: var(--aia-bx-bright-blue-5);
  }
`;

const SaveButtonText = styled.div`
  font-weight: bold;
  font-size: 14px;
  line-height: 24px;
  margin-left: 3px;
  text-wrap: nowrap;
  color: var(--aia-bx-dark-blue);
`;

const Img24 = styled.img`
  width: 24px;
  height: 24px;
`;

const Img32 = styled.img`
  width: 32px;
  height: 32px;
`;

const Terms = styled(Flex)`
  width: 100%;
  background-color: var(--aia-bx-gray-10);
  border-radius: 8px;
  padding: 8px 8px 8px 0px;
  gap: 16px;
  flex-direction: column;
`;

const LinkBtn = styled.span`
  color: var(--aia-bx-blue-2);
  cursor: pointer;
  text-decoration: underline;
  border: none;
  background: transparent;
  padding: 0;
  font-size: 14px;
`;

const Link = styled.a`
  color: var(--aia-bx-blue-2) !important;
  cursor: pointer !important;
  text-decoration: underline !important;
  border: none;
  background: transparent;
  padding: 0;
  font-size: 14px;
`;

const Circle = styled(Flex)`
  width: 20px;
  height: 20px;
  background-color: var(--aia-bx-dark-blue);
  color: var(--aia-bx-white);
  border-radius: 50%;
  font-weight: bold;
  justify-content: center;
  align-items: center;
`;

export default Step4;